import { useState, useCallback, useEffect } from 'react'
import {
  Button,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Input,
  useDisclosure,
  useColorMode,
  Box,
  Divider,
  Link,
  Code,
} from '@chakra-ui/react'
import { ExternalLink, Key, Sparkles, Copy, Check } from 'lucide-react'
import { toast } from 'sonner'
import { useCopyToClipboard } from '@/lib/hooks/useCopyToClipboard'
import { getStoredApiKey, setStoredApiKey } from '@/lib/storage/local-storage'
import ClaudeColor from '@lobehub/icons/es/Claude/components/Color'
import { generateApiKey, validateApiKey } from '@/lib/api/keys'

interface ClaudeConnectButtonProps {
  variant?: 'full' | 'compact'
  label?: string
  noIcon?: boolean
}

/**
 * Generate the Claude CLI command for MCP installation
 */
function generateClaudeCommand(apiKey: string): string {
  const origin = window.location.origin
  return `claude mcp add --transport http myrkup ${origin}/api/mcp \\
  --header "Authorization: Bearer ${apiKey}"`
}

export function ClaudeConnectButton({ variant = 'full', label, noIcon }: ClaudeConnectButtonProps) {
  const { colorMode } = useColorMode()
  const isDark = colorMode === 'dark'
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isCommandOpen, onOpen: onCommandOpen, onClose: onCommandClose } = useDisclosure()

  const [storedKey, setStoredKey] = useState<string | null>(null)
  const [inputKey, setInputKey] = useState('')
  const [isValidating, setIsValidating] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const { copy, hasCopied } = useCopyToClipboard()

  // Load stored API key on mount
  useEffect(() => {
    setStoredKey(getStoredApiKey())
  }, [])

  // Listen for API key changes
  useEffect(() => {
    const handleKeyChange = () => {
      setStoredKey(getStoredApiKey())
    }
    window.addEventListener('apikey-changed', handleKeyChange)
    return () => window.removeEventListener('apikey-changed', handleKeyChange)
  }, [])

  const handleCopyCommand = useCallback(() => {
    if (!storedKey) return
    copy(generateClaudeCommand(storedKey), 'command', 'Copied to clipboard')
  }, [storedKey, copy])

  const handleConnectClick = useCallback(() => {
    if (storedKey) {
      // Show command modal with stored key
      onCommandOpen()
    } else {
      // Open modal for key entry/generation
      onOpen()
    }
  }, [storedKey, onOpen, onCommandOpen])

  const handleValidateAndShowCommand = useCallback(async () => {
    if (!inputKey.trim()) {
      toast.error('Please enter an API key')
      return
    }

    setIsValidating(true)
    try {
      const result = await validateApiKey(inputKey.trim())
      if (result.valid) {
        setStoredApiKey(inputKey.trim())
        setStoredKey(inputKey.trim())
        window.dispatchEvent(new Event('apikey-changed'))
        onClose()
        // Show command modal
        onCommandOpen()
      } else {
        toast.error('Invalid API key')
      }
    } catch {
      toast.error('Failed to validate API key')
    } finally {
      setIsValidating(false)
    }
  }, [inputKey, onClose, onCommandOpen])

  const handleGenerateAndShowCommand = useCallback(async () => {
    setIsGenerating(true)
    try {
      const result = await generateApiKey({})
      if (result.apiKey) {
        setStoredApiKey(result.apiKey)
        setStoredKey(result.apiKey)
        window.dispatchEvent(new Event('apikey-changed'))
        toast.success('Account created!')
        onClose()
        // Show command modal
        onCommandOpen()
      }
    } catch {
      toast.error('Failed to create account')
    } finally {
      setIsGenerating(false)
    }
  }, [onClose, onCommandOpen])

  return (
    <>
      <Button
        onClick={handleConnectClick}
        size={variant === 'compact' ? 'sm' : 'md'}
        variant="outline"
        borderColor={isDark ? 'stone.600' : 'stone.300'}
        color={isDark ? 'stone.200' : 'stone.700'}
        leftIcon={noIcon ? undefined : <ClaudeColor size={16} />}
        _hover={{
          bg: isDark ? 'stone.700' : 'stone.100',
          borderColor: isDark ? 'stone.500' : 'stone.400',
        }}
      >
        {label ?? (variant === 'compact' ? 'Claude' : 'Connect to Claude')}
      </Button>

      {/* Setup Modal for users without stored API key */}
      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent bg={isDark ? 'stone.800' : 'white'}>
          <ModalHeader>
            <HStack spacing={2}>
              <ClaudeColor size={20} />
              <Text>Connect Myrkup to Claude</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={5} align="stretch">
              <Text fontSize="sm" color={isDark ? 'stone.400' : 'stone.600'}>
                Connect your Myrkup account to Claude Code CLI to create and manage
                encrypted markdown shares directly from your AI assistant.
              </Text>

              {/* Option 1: Use existing key */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  Sign in with existing API key
                </Text>
                <HStack>
                  <Input
                    placeholder="mk_..."
                    value={inputKey}
                    onChange={(e) => setInputKey(e.target.value)}
                    size="sm"
                    fontFamily="mono"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleValidateAndShowCommand()
                    }}
                  />
                  <Button
                    onClick={handleValidateAndShowCommand}
                    isLoading={isValidating}
                    size="sm"
                    colorScheme="brand"
                    leftIcon={<Icon as={Key} boxSize={3} />}
                  >
                    Connect
                  </Button>
                </HStack>
              </Box>

              <HStack>
                <Divider />
                <Text fontSize="xs" color={isDark ? 'stone.500' : 'stone.400'} whiteSpace="nowrap">
                  or
                </Text>
                <Divider />
              </HStack>

              {/* Option 2: Generate new key */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  Create a new anonymous account
                </Text>
                <Button
                  onClick={handleGenerateAndShowCommand}
                  isLoading={isGenerating}
                  size="sm"
                  variant="outline"
                  w="100%"
                  leftIcon={<Icon as={Sparkles} boxSize={4} />}
                >
                  Create account & connect
                </Button>
                <Text fontSize="xs" color={isDark ? 'stone.500' : 'stone.400'} mt={2}>
                  No email required. Your API key is stored locally.
                </Text>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter justifyContent="space-between">
            <Link
              href="/docs/mcp"
              target="_blank"
              fontSize="xs"
              color={isDark ? 'stone.500' : 'stone.400'}
              display="flex"
              alignItems="center"
              gap={1}
            >
              Learn more about MCP
              <Icon as={ExternalLink} boxSize={3} />
            </Link>
            <Button variant="ghost" onClick={onClose} size="sm">
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Command Modal - shows the CLI command to copy */}
      <Modal isOpen={isCommandOpen} onClose={onCommandClose} size="4xl" isCentered>
        <ModalOverlay />
        <ModalContent bg={isDark ? 'stone.800' : 'white'}>
          <ModalHeader>
            <HStack spacing={2}>
              <ClaudeColor size={20} />
              <Text>Connect to Claude Code</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color={isDark ? 'stone.400' : 'stone.600'}>
                Run this command in your terminal to add Myrkup as an MCP server in Claude Code:
              </Text>

              <Box
                bg={isDark ? 'stone.900' : 'stone.50'}
                borderRadius="md"
                p={4}
                fontFamily="mono"
                fontSize="sm"
                whiteSpace="pre-wrap"
                wordBreak="break-all"
                borderWidth="1px"
                borderColor={isDark ? 'stone.700' : 'stone.200'}
                position="relative"
              >
                <Code
                  display="block"
                  bg="transparent"
                  p={0}
                  fontSize="sm"
                  whiteSpace="pre-wrap"
                  wordBreak="break-all"
                >
                  {storedKey ? generateClaudeCommand(storedKey) : ''}
                </Code>
              </Box>

              <Button
                colorScheme="brand"
                onClick={handleCopyCommand}
                leftIcon={<Icon as={hasCopied('command') ? Check : Copy} boxSize={4} />}
                size="sm"
                alignSelf="flex-start"
              >
                {hasCopied('command') ? 'Copied!' : 'Copy command'}
              </Button>

              <Text fontSize="xs" color={isDark ? 'stone.500' : 'stone.400'}>
                After running the command, Claude Code will be able to create and manage encrypted markdown shares on your behalf.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter justifyContent="space-between">
            <Link
              href="/docs/claude"
              target="_blank"
              fontSize="xs"
              color={isDark ? 'stone.500' : 'stone.400'}
              display="flex"
              alignItems="center"
              gap={1}
            >
              View Claude Code docs
              <Icon as={ExternalLink} boxSize={3} />
            </Link>
            <Button variant="ghost" onClick={onCommandClose} size="sm">
              Done
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
